import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Field, Select } from '@/components/ui/Field'
import type { ChoiceOption } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { normaliseDecimal } from '@/features/finance/lines'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber } from '@/lib/format'
import type { AllocationTargetKind, Payment } from '@/types/domain'

/* Saying what a payment settles (AD-11).
 *
 * Allocation is EXPLICIT — never inferred by date, never FIFO, and not
 * "suggested" either. A pre-filled split that the user only has to confirm is
 * the same failure with a friendlier name: the platform would still have
 * guessed which invoice was paid, and the guess would still be wrong on the
 * case where it matters. So this sheet starts empty and every figure in it is
 * one somebody typed.
 *
 * It is a CONFIRMED-WRITE flow end to end (UX-DR-24). Nothing appears in the
 * allocation list until the server has accepted the whole request: the
 * conservation checks run under row locks against sums another Finance user may
 * be moving in the same instant, so a row drawn before the answer arrives is a
 * receivable the screen calls settled and the ledger does not.
 *
 * OUTBOUND ONLY since S-21. Money paid out settles this case's actual cost
 * lines, and the caller resolves them (`useAllocationTargets`) and hands them
 * in as `targets`. Money RECEIVED is allocated in the client payment sheet
 * (`ClientPaymentSheet`, allocate mode) against the case's invoices, debit
 * notes and tranches with what each still takes — the tranche-only picker this
 * sheet used to read for itself, beside a sentence saying invoices and debit
 * notes could not be settled yet, is gone with it.
 */

/** One row of the split editor. */
export interface AllocationSplitDraft {
  /** React's, never the wire's — stable across edits so a row above can be
   *  retyped without the focus jumping out of the row being edited. */
  key: string
  targetKind: AllocationTargetKind
  targetId: string
  amount: string
}

/** The targets the caller supplies — an outbound payment's cost lines.
 *
 *  The direction page's «Оплатить перевозчику» is what needs this (issue #35
 *  story 6): an OUTBOUND payment settles a cost line, and no tranche is a thing
 *  it can pay. The alternative was a second allocation sheet, which is a second
 *  place the conservation rule, the ceiling hint and the confirmed-write
 *  discipline would have to stay right.
 *
 *  A whole list rather than a filter or a kind flag, deliberately: resolving
 *  what a target is called and what it is worth needs a table this sheet has no
 *  business reading. The caller already holds it — the leg's money block — and
 *  passing the resolved options keeps this component a picker over choices
 *  rather than a second reader of somebody else's rows.
 */
export interface AllocationTargets {
  kind: AllocationTargetKind
  options: ChoiceOption[]
  /** The standing sentence under the picker — what these targets ARE. Not an
   *  error: nothing has gone wrong, so nothing about it is red. */
  notice?: string
  /** What to say when the list is EMPTY, which is a different fact from what
   *  the list is — no actual cost recorded, or every one already paid. The
   *  caller words it, because only it knows which. */
  emptyNotice: string
}

/** Accepts `1234.56`, `1 234,56` and `1,234.56`; rejects text and blanks.
 *
 *  A near-twin of the payment sheet's own check, and deliberately not shared
 *  with it: a component file may export only components (react-refresh), and a
 *  four-line regex is a poorer reason to add a module than the confusion of
 *  importing form validation from a file full of JSX. */
function isValidAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

function isSplitComplete(split: AllocationSplitDraft): boolean {
  return (
    split.targetId.trim() !== '' &&
    isValidAmount(split.amount) &&
    Number(normaliseDecimal(split.amount)) > 0
  )
}

function emptySplit(key: string, kind: AllocationTargetKind): AllocationSplitDraft {
  // Blank, not pre-filled with the unallocated balance, and no target chosen
  // even when the case has exactly one. Offering the whole remaining amount, or
  // pre-selecting the only tranche, is a guess about what this money settles —
  // the one thing AD-11 forbids the platform from making. A single-option picker
  // still costs one click, and that click is the record that a human decided.
  return { key, targetKind: kind, targetId: '', amount: '' }
}

export function PaymentAllocationSheet({
  open,
  onClose,
  payment,
  onSubmit,
  pending,
  /** What the SERVER refused, if it did — an over-allocation naming the ceiling
   *  it breached, or a target kind nothing has registered yet. Shown over the
   *  local hint: the server's answer is the more urgent news, and the fields it
   *  names may all look valid from here. */
  submitError,
  /** The cost lines this payment may settle — see `AllocationTargets`. */
  targets,
}: {
  open: boolean
  onClose: () => void
  payment: Payment
  onSubmit: (splits: AllocationSplitDraft[]) => void
  pending?: boolean
  submitError?: string
  targets: AllocationTargets
}) {
  const { t } = useTranslation()
  const kind = targets.kind
  const [draft, setDraft, touched, markTouched] = useFormDraft<AllocationSplitDraft[]>(
    // Keyed on the payment, so opening the sheet on a different payment starts
    // a fresh split rather than carrying the last one's rows across.
    open ? payment.id : null,
    () => [emptySplit('split-1', kind)],
  )

  const targetOptions = targets.options

  const currency = payment.amount.currency
  const unallocated = Number(payment.unallocated.amount)
  const requested = draft.reduce(
    (sum, split) => sum + (isSplitComplete(split) ? Number(normaliseDecimal(split.amount)) : 0),
    0,
  )

  /* The client-side ceiling check. It exists to spare a round trip and to put
     the number beside the fields that produced it — NOT as the guarantee. The
     guarantee is the server's, taken under `FOR UPDATE` against sums that may
     have moved since this sheet was opened (AD-11, AC 3), and a client that
     treated its own arithmetic as authoritative would be exactly the
     check-then-write race that acceptance criterion names. */
  const overAllocated = requested > unallocated
  const incomplete = draft.length === 0 || !draft.every(isSplitComplete)

  /* Nothing to allocate against is a legal state, not a fault — no actual cost
     recorded yet, or every one already paid. Blocking the submit is what stops
     the sheet accepting a split it knows nothing can resolve. The targets are
     ALREADY resolved by the caller, so there is no read here to be pending or
     to have failed. */
  const noTargets = targetOptions.length === 0
  const invalid = incomplete || overAllocated || noTargets

  const localError = touched
    ? overAllocated
      ? t('finance.allocations.exceedsBalance', {
          balance: formatMoney(payment.unallocated.amount, currency),
        })
      : incomplete
        ? t('finance.allocations.splitsRequired')
        : undefined
    : undefined

  /* The list's standing sentence, or why it is empty — neither is a mistake
     anybody made, so neither is red. */
  const targetNotice = noTargets ? targets.emptyNotice : targets.notice

  const update = (key: string, patch: Partial<AllocationSplitDraft>) =>
    setDraft(draft.map((split) => (split.key === key ? { ...split, ...patch } : split)))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.allocations.title', { reference: payment.reference })}
      submitLabel={t('finance.allocations.submit')}
      pending={pending}
      // A standing sentence, not a failure: nothing has gone wrong, so nothing
      // here is red.
      notice={targetNotice}
      /* Two sources, one slot: the server's refusal first, because it describes
         a request that has already been made, then the local hint. */
      error={submitError ?? localError}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      {/* The balance being spent, stated before the fields that spend it.
          Unallocated cash is legal (AD-11) — this figure is what makes it
          visible, and what the splits below are checked against. */}
      <div className="bg-surface-sunken flex flex-wrap gap-x-6 gap-y-2 rounded-[var(--radius-md)] p-3">
        <span className="flex flex-col gap-1">
          <span className="asa-eyebrow">{t('common.fields.amount')}</span>
          <span className="asa-num text-text-body text-[14px]">
            {formatMoney(payment.amount.amount, currency)}
          </span>
        </span>
        <span className="flex flex-col gap-1">
          <span className="asa-eyebrow">{t('finance.payments.allocated')}</span>
          <span className="asa-num text-text-body text-[14px]">
            {formatMoney(payment.allocated.amount, currency)}
          </span>
        </span>
        <span className="flex flex-col gap-1">
          <span className="asa-eyebrow">{t('finance.payments.unallocated')}</span>
          <span
            className={cn(
              'asa-num text-[14px] font-semibold',
              unallocated > 0 ? 'text-attention' : 'text-text-muted',
            )}
          >
            {formatMoney(payment.unallocated.amount, currency)}
          </span>
        </span>
      </div>

      {draft.map((split, index) => {
        const flagged = touched && !isSplitComplete(split)
        return (
          <div
            key={split.key}
            className={cn(
              'bg-surface-sunken border-line-hair flex flex-col gap-2.5 rounded-[var(--radius-md)] border p-2.5',
              flagged && 'border-negative/45',
            )}
          >
            <div className="flex items-start gap-2">
              {/* The target, as a picker over the case's cost lines. Searchable
                  is deliberately OFF: a case carries a handful of actuals, and
                  a filter box over a handful of options only gets in the way. */}
              <Field
                label={t('finance.allocations.target')}
                required
                className="flex-1"
                error={
                  flagged && split.targetId === ''
                    ? t('finance.allocations.targetRequired')
                    : undefined
                }
              >
                {(props) => (
                  <Select
                    value={split.targetId}
                    onChange={(targetId) => update(split.key, { targetId })}
                    placeholder={t('finance.allocations.targetPlaceholder')}
                    options={targetOptions}
                    aria-label={t('finance.allocations.targetN', { n: index + 1 })}
                    {...props}
                  />
                )}
              </Field>

              {/* Destructive, and icon-only for the reason the charge editor's
                  remove is: a labelled button per row would crowd out the
                  fields, and a removed split is undone by choosing it again. */}
              <button
                type="button"
                onClick={() => setDraft(draft.filter((row) => row.key !== split.key))}
                aria-label={t('finance.allocations.remove', { n: index + 1 })}
                title={t('finance.allocations.remove', { n: index + 1 })}
                className={cn(
                  'text-text-faint hover:text-negative hover:bg-negative-soft mt-[22px]',
                  'flex size-9 shrink-0 cursor-pointer items-center justify-center',
                  'rounded-[var(--radius-md)] transition-colors',
                )}
              >
                <Trash2 size={15} aria-hidden />
              </button>
            </div>

            {/* The split is in the PAYMENT's currency, which is why the code is
                shown and not chosen: money coming out of this balance cannot be
                denominated in something else, and offering a picker would
                invite a split that the balance it draws on cannot express. */}
            <Field
              label={t('common.fields.amount')}
              required
              hint={t('finance.allocations.amountHint', { currency })}
            >
              {(props) => (
                <Input
                  inputMode="decimal"
                  value={split.amount}
                  onChange={(e) => update(split.key, { amount: e.target.value })}
                  aria-label={t('finance.allocations.amountN', { n: index + 1 })}
                  className="asa-num"
                  {...props}
                />
              )}
            </Field>
          </div>
        )
      })}

      <div className="flex items-center justify-between gap-2 pt-0.5">
        <Button
          variant="secondary"
          size="sm"
          iconLeft={Plus}
          onClick={() =>
            setDraft([
              ...draft,
              // The index alone would be reused after a removal, and React
              // would keep the removed row's DOM node and its cursor.
              emptySplit(
                `split-${draft.length}-${Math.random().toString(36).slice(2, 8)}`,
                kind,
              ),
            ])
          }
        >
          {t('finance.allocations.add')}
        </Button>

        {/* The running total of what is about to be applied, beside the balance
            it is checked against. Display arithmetic only — the server recounts
            it under lock and its answer is the one that decides. */}
        {draft.length > 0 ? (
          <span className="flex items-baseline gap-2">
            <span className="asa-eyebrow">{t('finance.allocations.requested')}</span>
            <span
              className={cn(
                'asa-num text-[15px] font-semibold',
                overAllocated ? 'text-negative' : 'text-text-heading',
              )}
            >
              {formatNumber(String(requested), 2)}
              <span className="text-text-muted ml-1 text-[12px] font-normal">
                {currency}
              </span>
            </span>
          </span>
        ) : null}
      </div>
    </FormSheet>
  )
}
